import { ArrowsOutSimpleIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@shared/ui/Modal/Modal';
import { Media } from '@shared/ui/Media/Media';

import type { VehicleGalleryImage } from './types';
import styles from './VehicleGallery.module.css';

interface VehicleGalleryProps {
  images: VehicleGalleryImage[];
  /** Запасная подпись, когда у изображения нет alt-текста из каталога. */
  fallbackAlt: string;
  /**
   * Действие поверх снимка в правом верхнем углу — сердечко избранного
   * (FE-587). Только действие над объявлением: состояния экрана поверх
   * фотографии не рисуются (DESIGN_SYSTEM §5.5).
   */
  overlay?: ReactNode | undefined;
}

/** Порог свайпа в пикселях: ниже него жест считается случайным сдвигом пальца. */
const SWIPE_THRESHOLD_PX = 48;

/**
 * Сколько клеток в ряду миниатюр (макет 04). Если снимков больше, последняя
 * клетка — «+N weitere Fotos» и открывает полноэкранный просмотр с первого
 * скрытого снимка: ряд не прокручивается вбок (DESIGN_SYSTEM §4.4).
 */
const THUMB_SLOTS = 4;

/**
 * Галерея карточки автомобиля (MVP_PRD §18).
 *
 * Главное изображение, лента миниатюр, полноэкранный просмотр, счётчик кадров,
 * управление стрелками и свайп на мобильных. Пропорции зарезервированы, при
 * ошибке загрузки показывается нейтральная заглушка.
 *
 * Подписей о состоянии автомобиля здесь нет и быть не может: анализа фотографий
 * система не делает, поэтому «повреждений не найдено» — запрещённая формулировка
 * (MVP_PRD §2). Единственный текст рядом со снимком — его alt.
 */
export function VehicleGallery({ images, fallbackAlt, overlay }: VehicleGalleryProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const count = images.length;
  const current = images[index];

  const step = useCallback(
    (delta: number) => {
      setIndex((previous) => (previous + delta + count) % count);
    },
    [count],
  );

  // Стрелки работают, пока открыт полноэкранный просмотр: там снимок занимает
  // весь экран и мышью до миниатюр не дотянуться.
  useEffect(() => {
    if (!fullscreen || count < 2) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') step(-1);
      if (event.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [fullscreen, count, step]);

  if (count === 0 || current === undefined) {
    return (
      <div className={styles.gallery}>
        <div className={styles.stage}>
          <Media src={undefined} alt={fallbackAlt} ratio="4 / 3" priority />
          {overlay ? <div className={styles.overlay}>{overlay}</div> : null}
        </div>
      </div>
    );
  }

  const alt = current.alt ?? fallbackAlt;
  const counter = t('vehicle.gallery.counter', { current: index + 1, total: count });
  // Ряд миниатюр без прокрутки: все снимки, если помещаются, иначе три и
  // клетка «+N weitere Fotos».
  const overflow = count > THUMB_SLOTS;
  const thumbs = overflow ? images.slice(0, THUMB_SLOTS - 1) : images;
  const hidden = count - thumbs.length;

  return (
    <div className={styles.gallery}>
      <div
        className={styles.stage}
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={(event) => {
          const start = touchStartX.current;
          const end = event.changedTouches[0]?.clientX;
          touchStartX.current = null;
          if (start === null || end === undefined) return;
          const delta = end - start;
          if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
          step(delta < 0 ? 1 : -1);
        }}
      >
        <Media
          src={current.large_url ?? current.card_url ?? undefined}
          alt={alt}
          ratio="4 / 3"
          priority
          sizes="(max-width: 1023px) 100vw, 60vw"
          className={styles.stageImage}
        />

        {overlay ? <div className={styles.overlay}>{overlay}</div> : null}

        <button
          type="button"
          className={styles.expand}
          onClick={() => {
            setFullscreen(true);
          }}
        >
          <ArrowsOutSimpleIcon size={18} weight="bold" aria-hidden="true" />
          {t('vehicle.gallery.expand')}
        </button>

        {count > 1 ? (
          <>
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrowPrev}`}
              onClick={() => {
                step(-1);
              }}
              aria-label={t('vehicle.gallery.previous')}
            >
              <CaretLeftIcon size={20} weight="bold" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrowNext}`}
              onClick={() => {
                step(1);
              }}
              aria-label={t('vehicle.gallery.next')}
            >
              <CaretRightIcon size={20} weight="bold" aria-hidden="true" />
            </button>
            {/* Короткая форма «3 / 12» — на снимке, полная «Foto 3 von 12» —
                для скринридера: цифры через косую черту он читает плохо. */}
            <p className={`${styles.counter} tabular`} aria-live="polite">
              <span aria-hidden="true">
                {t('vehicle.gallery.counterShort', { current: index + 1, total: count })}
              </span>
              <span className="visually-hidden">{counter}</span>
            </p>
          </>
        ) : null}
      </div>

      {count > 1 ? (
        <ul className={styles.thumbs} aria-label={t('vehicle.gallery.thumbnails')}>
          {thumbs.map((image, position) => (
            <li key={image.id}>
              <button
                type="button"
                className={[styles.thumb, position === index && styles.thumbActive]
                  .filter(Boolean)
                  .join(' ')}
                aria-current={position === index}
                onClick={() => {
                  setIndex(position);
                }}
              >
                <Media
                  src={image.thumb_url ?? image.card_url ?? undefined}
                  alt={t('vehicle.gallery.thumbnailAlt', { position: position + 1 })}
                  ratio="4 / 3"
                />
              </button>
            </li>
          ))}
          {overflow ? (
            <li>
              <button
                type="button"
                className={[styles.thumb, styles.more].join(' ')}
                onClick={() => {
                  setIndex(thumbs.length);
                  setFullscreen(true);
                }}
              >
                <span className={`${styles.moreLabel} tabular`}>
                  {t('vehicle.gallery.more', { count: hidden })}
                </span>
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}

      <Modal
        open={fullscreen}
        onClose={() => {
          setFullscreen(false);
        }}
        labelledBy="vehicle-gallery-title"
        className={styles.viewer}
      >
        {/* Содержимое монтируется только на открытом просмотре: иначе крупный
            кадр грузился бы вместе со страницей, а счётчик и alt дублировались
            бы в дереве доступности. */}
        {fullscreen ? (
          <>
            <h2 id="vehicle-gallery-title" className="visually-hidden">
              {t('vehicle.gallery.title')}
            </h2>
            <Media
              src={current.large_url ?? current.card_url ?? undefined}
              alt={alt}
              ratio="4 / 3"
              priority
            />
            {count > 1 ? (
              <div className={styles.viewerBar}>
                <button
                  type="button"
                  className={styles.viewerButton}
                  onClick={() => {
                    step(-1);
                  }}
                  aria-label={t('vehicle.gallery.previous')}
                >
                  <CaretLeftIcon size={18} weight="bold" aria-hidden="true" />
                </button>
                <p className="tabular">{counter}</p>
                <button
                  type="button"
                  className={styles.viewerButton}
                  onClick={() => {
                    step(1);
                  }}
                  aria-label={t('vehicle.gallery.next')}
                >
                  <CaretRightIcon size={18} weight="bold" aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>
    </div>
  );
}
